import React, { useMemo } from 'react';

import { Chip, IconButton, Tooltip } from '@material-ui/core';
import clsx from 'clsx';
import { format } from 'date-fns';
import type { ReactNodeLike } from 'prop-types';
import { useTranslation } from 'react-i18next';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Table from 'components/Table';
import type { ProcessingActivity } from 'config/api/processingActivities/processingActivities.types';
import processing_activities_messages from 'messages/processing_activities_messages';
import type { TableDataMappingRow } from 'types/Table';

import isProcessingActivityDraftInProgress from '../../_shared/isProcessingActivityDraftInProgress';
import type { ProcessingActivitiesSort } from '../processingActivitiesFilters';
import useStyles from '../ProcessingActivitiesPage.styles';

const MAX_VISIBLE_LEGAL_BASIS_CHIPS = 2;

type Props = {
  data: ProcessingActivity[];
  emptyStateContent?: ReactNodeLike;
  loading: boolean;
  onArchive: (activity: ProcessingActivity) => void;
  onEditDraft: (activity: ProcessingActivity) => void;
  onOpenActivity: (activity: ProcessingActivity) => void;
  onSort: (key: string, direction: string) => void;
  sort: ProcessingActivitiesSort;
};

const formatDate = (value?: string | null) => (value ? format(new Date(value), 'yyyy-MM-dd') : '-');

const getMessageFromMap = (map: Record<string, string[]>, code: string, t: (message: string | string[]) => string): string => {
  const message = map[code];
  return message ? t(message) : code;
};

const formatReviewPriorityReasons = (reasons: string[], t: (message: string | string[]) => string): string =>
  reasons.map(reason => getMessageFromMap(processing_activities_messages.review_priority_reasons, reason, t)).join(', ');

const ProcessingActivitiesTable: React.FC<Props> = ({
  data,
  emptyStateContent,
  loading,
  onArchive,
  onEditDraft,
  onOpenActivity,
  onSort,
  sort,
}) => {
  const { t } = useTranslation();
  const styles = useStyles();

  const dataMapping: TableDataMappingRow<ProcessingActivity>[] = useMemo(
    () => [
      {
        id: 'name',
        label: t(processing_activities_messages.table.name),
        get: activity => (
          <div className={styles.nameCell}>
            {activity.name}
            {isProcessingActivityDraftInProgress(activity) && (
              <Chip
                aria-label={t(processing_activities_messages.draft_badge)}
                className={styles.draftBadge}
                label={t(processing_activities_messages.draft_badge)}
                size='small'
              />
            )}
          </div>
        ),
      },
      {
        id: 'processingRole',
        label: t(processing_activities_messages.table.processing_role),
        get: activity =>
          activity.processingRole
            ? getMessageFromMap(processing_activities_messages.processing_role, activity.processingRole, t)
            : t(processing_activities_messages.review_placeholder),
      },
      {
        id: 'legalBasis',
        blockSorting: true,
        label: t(processing_activities_messages.table.legal_basis),
        get: activity => {
          if (!activity.legalBasis.length) {
            return <span>{t(processing_activities_messages.review_placeholder)}</span>;
          }

          const visibleBases = activity.legalBasis.slice(0, MAX_VISIBLE_LEGAL_BASIS_CHIPS);
          const hiddenCount = activity.legalBasis.length - visibleBases.length;
          const allLabels = activity.legalBasis.map(code => getMessageFromMap(processing_activities_messages.legal_basis, code, t));

          return (
            <Tooltip title={allLabels.join(', ')}>
              <div className={styles.legalBasisCell}>
                {visibleBases.map(code => (
                  <Chip
                    key={code}
                    className={styles.legalBasisChip}
                    label={getMessageFromMap(processing_activities_messages.legal_basis, code, t)}
                    size='small'
                  />
                ))}
                {hiddenCount > 0 && (
                  <Chip
                    className={styles.overflowChip}
                    label={t(processing_activities_messages.table.legal_basis_overflow, { count: hiddenCount })}
                    size='small'
                  />
                )}
              </div>
            </Tooltip>
          );
        },
      },
      {
        id: 'publishedVersionNumber',
        label: t(processing_activities_messages.table.current_version),
        get: activity =>
          activity.publishedVersionNumber != null
            ? t(processing_activities_messages.table.version_label, { number: activity.publishedVersionNumber })
            : '-',
      },
      {
        id: 'linkedAssetsCount',
        label: t(processing_activities_messages.table.linked_assets),
        get: activity => activity.linkedAssetsCount,
      },
      {
        id: 'review',
        label: t(processing_activities_messages.table.review_responses),
        get: activity =>
          activity.review
            ? t(processing_activities_messages.table.review_format, {
                answered: activity.review.answered,
                included: activity.review.included,
              })
            : t(processing_activities_messages.review_placeholder),
      },
      {
        id: 'dpiaIndicationsCount',
        label: t(processing_activities_messages.table.dpia_indications),
        get: activity => activity.dpiaIndicationsCount,
      },
      {
        id: 'reviewPriority',
        label: t(processing_activities_messages.table.review_priority),
        get: activity => {
          const label = getMessageFromMap(processing_activities_messages.review_priority, activity.reviewPriority, t);
          const reasons = formatReviewPriorityReasons(activity.reviewPriorityReasons, t);
          const priorityClassName = clsx(styles.priorityLabel, {
            [styles.priorityNormal]: activity.reviewPriority === 'normal',
            [styles.priorityElevated]: activity.reviewPriority === 'elevated',
            [styles.priorityHigh]: activity.reviewPriority === 'high',
          });

          if (!reasons) {
            return <span className={priorityClassName}>{label}</span>;
          }

          return (
            <Tooltip title={reasons}>
              <span className={priorityClassName}>{label}</span>
            </Tooltip>
          );
        },
      },
      {
        id: 'status',
        label: t(processing_activities_messages.table.status),
        get: activity => {
          const label = getMessageFromMap(processing_activities_messages.status, activity.status, t);
          return (
            <span className={styles.statusCell}>
              <span
                aria-hidden='true'
                className={clsx(styles.statusDot, {
                  [styles.statusDotPublished]: activity.status === 'published',
                  [styles.statusDotArchived]: activity.status === 'archived',
                })}
              />
              {label}
            </span>
          );
        },
      },
      {
        id: 'updatedAt',
        label: t(processing_activities_messages.table.updated),
        get: activity => <span className={styles.noWrap}>{formatDate(activity.updatedAt)}</span>,
      },
      {
        id: 'actions',
        blockSorting: true,
        isAddon: true,
        label: '',
        get: activity => (
          <div className={styles.actionsCell}>
            <ContextMenu
              elements={[
                { label: t(processing_activities_messages.actions.open), onClick: () => onOpenActivity(activity) },
                ...(isProcessingActivityDraftInProgress(activity)
                  ? [{ label: t(processing_activities_messages.actions.edit_draft), onClick: () => onEditDraft(activity) }]
                  : []),
                { label: t(processing_activities_messages.actions.archive), onClick: () => onArchive(activity) },
              ]}
              id={`processing-activity-${activity.id}`}
            >
              <IconButton aria-label={t(processing_activities_messages.table.actions)} size='small'>
                <MoreIcon />
              </IconButton>
            </ContextMenu>
          </div>
        ),
      },
    ],
    [onArchive, onEditDraft, onOpenActivity, styles, t],
  );

  return (
    <div
      aria-busy={loading}
      aria-label={t(processing_activities_messages.table.aria_label)}
      className={styles.tableScrollWrapper}
      role='region'
    >
      <Table
        clickableRow
        data={data}
        dataMapping={dataMapping}
        emptyStateContent={emptyStateContent}
        onRowClick={(_, activity) => onOpenActivity(activity)}
        onSort={onSort}
        refetching={loading}
        rowAriaLabel={activity => t(processing_activities_messages.table.row_aria_label, { name: activity.name })}
        sort={sort}
      />
    </div>
  );
};

export default ProcessingActivitiesTable;
